import { useQuery } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { isApiError } from "@/api/errors";
import { qk } from "@/api/queryKeys";
import { testerApi } from "@/api/testerApi";
import type { Health, Version } from "@/api/types";
import { REQUIRED_API_VERSION, REQUIRED_PROTOCOL } from "@/lib/buildInfo";
import type { RuntimeState } from "@/lib/status";
import { toasts } from "@/lib/toasts";

export interface RuntimeInfo {
  state: RuntimeState;
  health?: Health;
  version?: Version;
  activeRunId?: string;
  canCommand: boolean;
  refetch: () => void;
}

const Ctx = createContext<RuntimeInfo | null>(null);

async function localNetworkDenied(): Promise<boolean> {
  try {
    const status = await navigator.permissions?.query({ name: "local-network-access" as PermissionName });
    return status?.state === "denied";
  } catch {
    return false;
  }
}

type Probe = { kind: "ok"; health: Health } | { kind: "network"; denied: boolean } | { kind: "foreign" };

async function probeRuntime(): Promise<Probe> {
  try {
    const health = await testerApi.health();
    if (!health || typeof health !== "object" || !("status" in health)) return { kind: "foreign" };
    return { kind: "ok", health };
  } catch (e) {
    if (isApiError(e) && e.code === "NETWORK") return { kind: "network", denied: await localNetworkDenied() };
    if (isApiError(e)) return { kind: "ok", health: { status: "degraded", degraded_reason: e.message } as Health };
    return { kind: "foreign" };
  }
}

export function RuntimeProvider({ children, probe = true }: { children: ReactNode; probe?: boolean }) {
  const [connected, setConnected] = useState(false);
  const health = useQuery({
    queryKey: qk.health(),
    queryFn: probeRuntime,
    enabled: probe,
    refetchInterval: connected ? 30_000 : 5_000,
    refetchIntervalInBackground: false,
    retry: false,
    staleTime: 0,
  });
  // The Tester API reports engine protocol 0 until the engine answers, so only a ready runtime's version counts.
  const ready = health.data?.kind === "ok" && health.data.health.status === "ready";
  const version = useQuery({ queryKey: qk.version(), queryFn: testerApi.version, enabled: probe && ready, retry: false, staleTime: 0 });

  const state: RuntimeState = useMemo(() => {
    const d = health.data;
    if (!d) return "starting";
    if (d.kind === "network") return d.denied ? "permission_blocked" : "not_running";
    if (d.kind === "foreign") return "port_conflict";
    const s = d.health.status;
    if (s === "starting") return "starting";
    if (s === "recovering") return "recovering";
    if (s === "degraded") return "unhealthy";
    const v = version.data;
    if (
      v &&
      (v.api_version !== REQUIRED_API_VERSION || (v.engine_protocol_version !== 0 && v.engine_protocol_version !== REQUIRED_PROTOCOL))
    )
      return "incompatible";
    return "connected";
  }, [health.data, version.data]);

  const wasDown = useRef(false);
  useEffect(() => {
    const isUp = state === "connected";
    setConnected(isUp);
    if (isUp && wasDown.current) toasts.runtimeConnected();
    if (!isUp && health.data) wasDown.current = true;
  }, [state, health.data]);

  const refetchHealth = health.refetch;
  const value = useMemo<RuntimeInfo>(
    () => ({
      state,
      health: health.data?.kind === "ok" ? health.data.health : undefined,
      version: version.data,
      activeRunId: health.data?.kind === "ok" ? (health.data.health.active_run_id ?? undefined) : undefined,
      canCommand: state === "connected",
      refetch: () => void refetchHealth(),
    }),
    [state, health.data, version.data, refetchHealth],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useRuntime(): RuntimeInfo {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useRuntime outside RuntimeProvider");
  return ctx;
}
